import {RequestStatuses} from '~/app/data/statuses/request';
import {OrderStatuses} from '~/app/data/statuses/order';
import {IssueTypes, IssueStatuses, PartTypes} from '~/app/data/statuses/issue';
import { OperatingTypes } from '~/app/data/statuses/operatingData';
import { MessageType } from '~/app/data/statuses/chat';
import { MachineTabs } from '~/app/data/statuses/machine';
import { MaterialIcons } from '~/app/common/Icon/icons';
import { FieldValue } from '~/app/modules/form/store';
import { Languages, LanguagesFlags } from '~/app/data/statuses/i18n';
import { TFunction } from 'i18next';
import { ServiceStatuses } from '~/app/data/statuses/services';
import { Training } from '~/training/types';

// Asset ===============================================================================================================
export interface Asset {
    id: number
    is_active: boolean
    created_at: string
    updated_at: string
    code: string
    name: string
    description: string
    status: Status
    asset_type: AssetType
    category: Category
    model: Model
    model_year: string
    serial_number: string
    licence_plate_number: string
    euro_class: string
    purchase_date: string
    purchase_price: number
    delivery_date: string
    delivery_note: string
    usage_date: string
    owner_name: string
    location: string
    country: string
    origin: string
    current_meter: number
    meter_type: number
    meter_unit: string
    primary_asset_person: AssetPerson
    temporary_asset_person: AssetPerson
    barcode: string
    color: string
    weight: number
    chassis: string
    manufacturer: string
    manufacturer_address: string
    vendor: string
    contract_number: string
    contract_date: string
    customs_category: string
    customs_date: string
    warrenty_number: string
    warranty_start_date: string
    warranty_end_date: string
    maintenance_company: string
    maintenance_contract: string
    maintenance_company_address: string
    maintenance_company_phone: string
    maintenance_company_email: string
    maintenance_start_date: string
    maintenance_end_date: string
    cargo_note: string
    depreciation_age: number
    depreciation_start_date: string
    start_depreciation_value: number
    parent_id: number
    is_rental: boolean
    statuses_override: {
        id: number
        asset_id: number
        status_id: number
        starts_at: string
        ends_at: string
        description: string
    }[]
}

export interface AssetPayload {
    id?: number
    code: string
    name: string
    description?: string
    is_active?: boolean
    status_id: number
    asset_type_id: number
    category_id: number
    model_id?: number
    model_year?: string
    serial_number?: string
    licence_plate_number?: string
    euro_class?: string
    purchase_date?: string
    purchase_price?: number
    delivery_date?: string
    delivery_note?: string
    usage_date?: string
    owner_name?: string
    location?: string
    country?: string
    origin?: string
    current_meter?: number
    meter_type?: number
    meter_unit?: string
    primary_employee_id?: number
    current_employee_id?: number
    barcode?: string
    color?: string
    weight?: number
    chassis?: string
    manufacturer?: string
    manufacturer_address?: string
    vendor?: string
    contract_number?: string
    contract_date?: string
    customs_category?: string
    customs_date?: string
    warrenty_number?: string
    warranty_start_date?: string
    warranty_end_date?: string
    maintenance_company?: string
    maintenance_contract?: string
    maintenance_company_address?: string
    maintenance_company_phone?: string
    maintenance_company_email?: string
    maintenance_start_date?: string
    maintenance_end_date?: string
    cargo_note?: string
    depreciation_age?: number
    depreciation_start_date?: string
    start_depreciation_value?: number
    parent_id?: number
    primary_asset_person_id?: AssetPerson['id']
    temporary_asset_person_id?: AssetPerson['id']
    is_rental?: boolean
}

export interface AssetFilter {
    withStatus?: boolean
    statusFromDate?: string
    statusToDate?: string
    page?: number
    results?: number
    orderBy?: string
    sortOrder?: string
}

// Asset Person ========================================================================================================
export interface AssetPerson {
    id: number
    name: string
    email: string
    phone: string
    mobile: string
    job_title: string
    section: string
    user_id: string
    is_active: boolean
    type: number
}

export interface AssetPersonPayload {
    id?: number
    name?: string
    email?: string
    phone?: string
    mobile?: string
    job_title?: string
    section?: string
    user_id?: string
    is_active?: boolean
    type?: number
}

export enum AssetPersonReplacePersonType {
    PRIMARY,
    TEMPORARY
}

export interface AssetPersonReplacePersonPayload {
    asset_id: Asset['id']
    asset_person_id: AssetPerson['id']
    asset_person_type: AssetPersonReplacePersonType
}

// AssetType ===========================================================================================================
export interface AssetType {
    id: number
    code: string
    name: string
    description: string
    created_at: string
    updated_at: string
    is_active: boolean
}

export interface AssetTypePayload {
    id?: number
    code?: string
    name?: string
    description?: string
    is_active?: boolean
}

// Manufacturer ========================================================================================================
export interface Manufacturer {
    id: number
    code: string
    name: string
    description: string
    created_at: string
    updated_at: string
    is_active: boolean
    models?: Model[]
}

export interface ManufacturerPayload {
    id?: number
    code?: string
    name?: string
    description?: string
    is_active?: boolean
}

// Model ===============================================================================================================
export interface Model {
    id: number
    code: string
    name: string
    description: string
    created_at: string
    updated_at: string
    is_active: boolean
    manufacturer: Manufacturer
}

export interface ModelPayload {
    id?: number
    code?: string
    name?: string
    description?: string
    manufacturer_id?: number
    is_active?: boolean
}

// Status ==============================================================================================================
export interface Status {
    id: number
    code: string
    name: string
    description: string
    created_at: string
    updated_at: string
    is_active: boolean
}

export interface StatusPayload {
    id?: number
    code?: string
    name?: string
    description?: string
    is_active?: boolean
}

// Cargo Types =========================================================================================================
export interface CargoType {
    id: number
    name: string
    metric_unit: string
}

export interface CargoTypePayload {
    id?: number
    name?: string
    metric_unit?: string
}

// Category ====================================================================================================
export interface Category {
    id: number
    code: string
    name: string
    description: string
    created_at: string
    updated_at: string
    is_active: boolean
    parent_id: number
    children?: Category[]
}

export interface CategoryPayload {
    id?: number
    code?: string
    name?: string
    description?: string
    is_active?: boolean
    parent_id?: number
}

// Delivery time range =================================================================================================
export interface DeliveryTimeRangeValue {
    has_value: boolean
    value: {
        hours: number
        minutes: number
        seconds: number
        milliseconds: number
        ticks: number
        days: number
        total_days: number
        total_hours: number
        total_milliseconds: number
        total_minutes: number
        total_seconds: number
    }
}

export interface DeliveryTimeRange {
    id: number
    name: string
    starts_at: string
    ends_at: string
    readonly: boolean
}

export interface DeliveryTimeRangeResponse {
    id: number
    name: string
    starts_at: DeliveryTimeRangeValue
    ends_at: DeliveryTimeRangeValue
}

// Orders ==============================================================================================================
export interface OrderCargo {
    id: string
    order_cargo_type: CargoType
    cargo_qty: number
    placement: string
}

export interface OrderCargoPayload {
    id?: string
    cargo_qty?: number
    cargo_type_id?: CargoType['id']
    placement?: string
}

export interface Order {
    id: string
    number: number,
    created_by: string
    created_date: string
    created_user_first_name: string
    created_user_last_name: string
    updated_by: string
    updated_date: string
    updated_user_first_name: string
    updated_user_last_name: string
    is_read: boolean
    transport_request: Request
    asset: Asset
    asset_person: AssetPerson
    external_project: Project
    starts_at: string
    ends_at: string
    order_cargo_items: OrderCargo[] | null
    status: OrderStatuses
    comment: string
}

export interface OrderPayload {
    id?: string
    number?: number
    is_read?: boolean
    request_id?: Request['id']
    asset_id?: Asset['id']
    asset_person_id?: AssetPerson['id']
    external_project_id?: Project['id']
    starts_at?: string
    ends_at?: string
    order_cargo_items?: OrderCargoPayload[]
    status?: OrderStatuses
    comment?: string
}

export interface OrderFilter {
    id?: string
    userId?: string | null
    transportRequestId?: Request['id']
    fromDate?: string
    toDate?: string
    page?: number
    results?: number
    orderBy?: string
    sortOrder?: 'asc' | 'desc'
}

// Projects ============================================================================================================
export interface Project {
    id: string
    name: string
    number: string
    image?: string
    isFavorite?: boolean
    isLastUsed?: boolean
    chat: Message[]
    chatUsers: SearchUser[]
}

export interface ProjectPayload {
    id?: string
    name?: string
    code?: string
}

// Request Types =======================================================================================================
export enum RequestTypeTypes {
    IN,
    OUT,
    INSIDE
}

export interface RequestType {
    id: number
    name: string
    type: RequestTypeTypes
}

export interface RequestTypePayload {
    id?: number
    name?: string
    type?: RequestTypeTypes
}

// Cargo ===============================================================================================================
export interface Cargo {
    id: number
    request_cargo_type: CargoType
    cargo_qty: number
    external_project: Project
}

export interface CargoPayload {
    id?: number
    cargo_type_id?: CargoType['id']
    cargo_qty?: number
    external_project_id?: Project['id']
}

// Request =============================================================================================================
export interface Request {
    id: string
    created_date: string
    created_by: string
    created_user_first_name: string
    created_user_last_name: string
    updated_date: string
    updated_by: string
    updated_user_first_name: string
    updated_user_last_name: string
    number: number
    request_type: RequestType
    request_status: RequestStatus
    delivery_time_range: DeliveryTimeRange
    request_project: Project
    vehicle_category: Category
    vehicle_qty: number
    delivery_time_range_id: number
    starts_at: string
    ends_at: string
    extra_time_need: number
    extra_notes: string
    request_items: Cargo[] | null
    count_of_orders: number
}

export interface RequestPayload {
    id?: string
    number?: number
    request_type_id?: RequestType['id']
    request_status_id?: RequestStatus['id']
    delivery_time_range_id?: DeliveryTimeRange['id']
    project_id?: Project['id']
    category_id?: Category['id']
    vehicle_qty?: number
    starts_at?: string
    ends_at?: string
    extra_time_need?: number
    extra_notes?: string
    request_items?: CargoPayload[]
}

export interface RequestFilter {
    id?: string
    userId?: string | null
    fromDate?: string
    toDate?: string
    projects?: Project['id'][]
    vehicleCategories?: Category['id'][]
    cargoTypes?: CargoType['id'][]
    statuses?: RequestStatuses[]
    page?: number
    results?: number
    orderBy?: string
    sortOrder?: 'asc' | 'desc'
    $filter?: string | { search: string, searchKey?: string }
}

// Request Statuses ====================================================================================================
export interface RequestStatus {
    id: RequestStatuses
    name: string
}

// Permission ==========================================================================================================
export interface Permission {
    id: number
    name: string
    menuName: string
    status: boolean
    parentId: number
    parentName: string
}

export interface MenuPermission extends Permission {
    subMenu?: {
        [key: string]: SubMenuPermission
    }
}

export interface SubMenuPermission extends Permission {
    actions?: {
        [key: string]: ActionPermission
    }
}

export interface ActionPermission extends Permission {
    subActions?: {
        [key: string]: Permission
    }
}

export interface Permissions {
    [key: string]: MenuPermission
}

export interface Settings {
    [key: string]: {} | ""
}

export interface PermissionResponse {
    roleId: number
    roleName: string
    permissions: Permissions
    appSettings: Settings
    tenantSettings: Settings
}

export interface PermissionOffline {
    userId: string
    roleId: number
    roleName: string
    permissions: Permissions
    appSettings: Settings
}

export interface PermissionPayload {
    object_id: number
    permission: number
}

// Role ================================================================================================================
export interface Role {
    id: number
    name: string
    description: string
}

export interface RolePayload {
    id?: number
    name: string
    description: string
    is_admin: boolean
    role_permissions: PermissionPayload[]
}

// User ================================================================================================================
export interface Language {
    value: Languages,
    flag: LanguagesFlags,
    label: string
}

export interface UserPreferences {
    language: Languages
}

export interface User {
    id: string
    username: string
    name: string
    email: string
    employee_numner: string
    phone: string
    mobile: string
    job_title: string
    department: string
    departments: number[]
    is_active: boolean
    language: Languages
    languages: any
    expressions: any
    created_at: string
    updated_at: string
    webSocketURL?: string
    isInternalUser?: boolean
    avatar?: string
    image?: string
}

export interface SearchUser {
    id: string
    name: string
    email: string
    phone: string
    avatar: string
    added: boolean
    deleted: boolean
    new: boolean
}

export interface SearchUserPayload {
    searchText: string,
    offset: number,
    isForTag?: boolean
    machineId?: string,
    department?: number
}

export interface SearchUserResponse {
    users: SearchUser[],
    page: number,
    total: number
}

export interface UserPayload {
    id?: string
    auth_id?: string
    name: string
    email: string
    is_active: boolean
    phone?: string
    mobile?: string
    job_title?: string
    department?: string
    language?: string
}

export interface LoginPayload {
    username?: string
    password?: string
    remember_me?: boolean
}

export interface UserWithTokenPayload {
    token: string,
    expires_in: number
    data: User
    type?: string
}

export interface LoginResponse {
    code: string | number
    payload: UserWithTokenPayload
}

export interface AllUsers {
    id: number
    name: string
}

export interface LanguageDetail {
    id: string;
    name: string;
    locale: Languages.ENGLISH | Languages.NORWEGIAN;
}

export interface Expression {
    id: number;
    currentText: string;
    newText: string;
    language: string;
}

export interface TranslationObject {
    [key: string]: string | TranslationObject;
}

export interface ChangeLanguage {
    (language: Languages): Promise<TFunction>
}
// Periodic Check ============================================================================================================
export interface PeriodicCheck {
    id: string
    name: string
    nameEn?: string | null
    type: string
    isImageRequired: boolean
    comment?: string
    excavator?: {}
    groupId?: string
    fluidTypeId?: string,
    isRequired: boolean
}

export interface LastPeriodicCheck {
    id: string
    userId: User['id']
    userName: User['name']
    date?: string,
    status?: number
    /** Local-only: completed check is stored on device and waiting for sync. */
    pendingSync?: boolean
}

// issues
export interface Issue {
    id: string
    type: IssueTypes
    description: string
    status: IssueStatuses
    statusDate: string
    labels: string
    dueDate: string | null
    categoryName?: string | null
    guid?: string
    /** Local-only: issue saved on device and waiting for sync. */
    pendingSync?: boolean
}

export interface Service {
    id: string
    unitText: string
    serviceOccurrenceText: string
    serviceText: string
    serviceStatus: any;
    intervalTypeText: string
    dueHours: string
    dateDiff: number
    status: ServiceStatuses
}

export interface WorkDetails {
    date: string
    comment: string | null
    name: string
    hours: string
    cost: string
}

export interface workOrderCost {
    overtime1Percentage: string | null
    overtime2Percentage: string | null
}

export interface Message {
    user: string
    senderId: string
    senderName: User['name']
    issueId: string
    message: string
    dateTime?: string
    time?: string
    actions?: number | null
    showDate?: boolean
    type: MessageType
    machineId?: string
    projectId?: string
    attachmentUrls?: string[]
}

export interface NotificationMessage extends Message {
    machineId: string
    intern: string
    category: string
    subcategory: string
    senderImage: string
    issueDescription: string
    messageStatus: boolean
    importance: number
}

export interface Document {
    data: string | Archive;
    machineId: string;
    documentId: string | number;
    downloadLink?: string;
    filePath?: string;
}

export interface IssueDetails extends Issue {
    costCategoryId?: number | null
    comment: string | null
    seenDate: string | null
    images: string[]
    files?: string[]
    categoryId: string | null
    categoryName: string | null
    issueNo: string
    machineId: string
    machineIntern: string
    machineName: string
    departmentName: string | null
    departmentId: string | null
    departmentPrefix: string | null
    issueDepartmentName: string | null
    issueDepartmentId: string | null
    workOrderId: string | null,
    labels: string,
    workOrderNo: string | null
    workDetails: WorkDetails[]
    workOrderCost: workOrderCost
    chat: Message[]
    chatUsers: SearchUser[]
    createdAt: string
    updatedAt: string
    createdBy: string
    closedAt: string | null
    closedBy: string | null
    issueCreatedUserId: string | null
    issueCreatedBy: string | null
    issueUpdatedBy: string | null
    projectId: string | null
    guid?: string
    isCreatedFromOfflineMode?: boolean
    sortingType?: string | null
    hpCode?: string | null
    hasSimplifiedWorkorderEnabled?: string | null
    registrationDate: string
    parts?: Part[]
    materials?: Material[]
}

export interface ServiceDetails extends Service {
    serviceText: string
    costCategoryId?: number | null
    workOrderId: string | null
    serviceIntervalId: string
    statusDate: string;
    machineId: string
    machineIntern: string
    machineName: string
    departmentPrefix: string | null
    departmentName: string | null
    departmentId: string | null
    workOrderNo: string | null
    workDetails: WorkDetails[]
    workOrderCost?: workOrderCost
    chat: Message[]
    chatUsers: SearchUser[]
    checklist: ServiceCheck[]
    createdAt: string
    updatedAt: string
    serviceCreatedBy: string | null
    serviceUpdatedBy: string | null
    scheduleAt: Date | null
    primaryMeterUnit: number | null
    secondaryMeterUnit:number | null
    intervalType: number | null
    serviceProgramType: number | null
    timeUnit: number | null
    operatingTime: number
    projectId: string | null
    guid?: string
    serviceProgramName: string
    dateDiff: number,
    isCreatedFromOfflineMode?: boolean
    sortingType?: string | null
    hpCode?: string | null
    hasSimplifiedWorkorderEnabled?: string | null
    alertScheduleDays: number
    alertServiceOccurrence: number
    registrationDate: string
    parts?: Part[]
    materials?: Material[]
}

export interface DeleteServiceResponse {
    success: boolean
    msg: string
}

export interface ServiceCheck {
    id: number,
    taskId: number,
    name: string,
    /** 0 cancel, 1 OK, 2 N/A, null not performed. Periodic checklist uses a different mapping. */
    status: 0 | 1 | 2 | null,
    sectionId: number,
    comment: string,
    isServiceSection: boolean,
    sequenceNumber: string
    issues: string[]
    isRequired?: boolean
}

export interface Part {
    id: string,
    workOrderPartId?: string,
    article_no: string,
    article_name: string,
    in_stock: number,
    price_per_unit: number,
    quantity: number,
    type: PartTypes,
    location?: string,
    deleted?: boolean,
    added?: boolean,
    customerDiscount?: number
}

export interface Material {
    id: number | string,
    issueId: number,
    fromType: number,
    name: string | null,
    categoryId: number | null,
    categoryName?: string | null,
    comment: string | null,
    pricePerUnit: number,
    type: PartTypes,
    quantity: number,
    costId?: string | null,
    deleted?: boolean,
    added?: boolean,
    customerDiscount?: number
}

export interface MiscMaterial {
    id: number,
    costId: string,
    name: string,
    categoryId: number,
    categoryName: string,
    pricePerUnit: number
}

export interface SaveMaterialItem {
    id: number | string,
    costId: string | null,
    name: string | null,
    pricePerUnit: number,
    quantity: number,
    comment: string | null,
    added?: boolean,
    deleted?: boolean,
    customerDiscount?: number
}

export interface SaveMaterialPayload {
    id: string,
    materials: SaveMaterialItem[]
}

export interface SaveMaterialResponse {
    success?: boolean
    message?: string
}

export interface IssueDetailsTable {
    id: string;
    machineId: string;
    data: string | IssueDetails;
    images: string | string[]
}

export interface IssueCount {
    type: Issue['type']
    count: number
}

export interface IssueCategory {
    id: number
    name: string
}

export interface ServiceDetailsTable {
    id: string;
    machineId: string;
    data: string | ServiceDetails;
}

export interface Department {
    id: number
    name: string
}

export interface IssueSortingType {
    id: number
    name: string
}

export interface Label {
    id: number
    name: string
    colorName: string
}

export interface HPCode {
    id: number
    code: string
    name: string
}

export interface WorkType {
    id: number
    name: string
}

export interface CostCategory {
    id: number
    name: string
    description: string
    displayCustomer: number
    displayWork: number
    isValidateIntern: number 
    needsSorting: number 
    discountOnParts: number 
    discountOnTimerAndMaterials: number 
    chargeDepartment: number
    costData: {
        overtime1Percentage: number
        overtime2Percentage: number
    } | null
}

export interface Customer {
    id: number
    customerNumber: string
    customerName: string
    displayProject: number
    needsSorting: number
    isInvoiceAcceptance?: number
    isInvoiceAcceptanceRequired?: number
    customerDiscount?: number
}

// 0 - SK, 1 = PKK, '' = No Certification, 3 = ADR, 4 = Løftekontroll, 5 = elektrisk kontroll, 6 = PSV, 7 = Luft tank 1 år, 8 = Slukke utstyr, 9 = Luft tank 2 år, 10 = Elektrisk motor, 11 = NDT, 12 = Luft tank 10 år
export interface Certification {
    type: 0 | 1 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | '' 
    lastDate: string
    nextDate: string
    isApproved: boolean
}

// Machines ============================================================================================================

export interface MachineActiveTabs {
    activeTab: MachineTabs
}

export interface MachineSearch {
    id: string
    name: string
    intern: string
    regNo: string
    serialNo: string
    assignedDriverName?: string
    fixedDriverName?: string
    currentDriverName?: string
    driverName?: string
    assignedDriverRole?: 'fixedDriverName' | 'currentDriverName' | 'driverName' | ''
}

export interface SearchMachine {
    id: string
    name: string
    intern: string
    assignedDriverName?: string
    isDeleteRestricted?: boolean
}

export interface SearchMachinePayload {
    searchText: string,
    offset: number
}

export interface SearchMachineResponse {
    machines: SearchMachine[],
    page: number,
    total: number
}

export interface Machine {
    id: string
    name: string
    intern: string
    regNo: string
    isFavorite?: boolean
    isLastUsed?: boolean
    categoryImage?: string
    machineImage?: string
    assignedDriverName?: string
    isOperatingTimeVisible?: number
    operatingTimeAllowed?: number
    operatingTime?: string
    isOperatingCountVisible?: number
    operatingCountAllowed?: number
    operatingCount?: string
    operatingType?: string
    lastOperatingTimeDate?: string
    lastOperatingCountDate?: string
    /** Local-only: operating time saved on device and waiting for sync. */
    operatingTimePendingSync?: boolean
    /** Local-only: operating count saved on device and waiting for sync. */
    operatingCountPendingSync?: boolean
    lastDailyCheck?: LastPeriodicCheck
    lastAcceptanceInspection?: LastPeriodicCheck
    categoryType?: string
    checks: PeriodicCheck[]
    acceptanceInspectionChecks: PeriodicCheck[]
    checkData?: PeriodicCheckDraftResponse
    checkId?: string
    inspectionType: number
    issuesCount: IssueCount[]
    issues: Issue[],
    services: Service[],
    certification: Certification
    adrCertification: Certification
    liftControlCertification: Certification
    electricalControlCertification: Certification
    psvCertification: Certification
    airTank1YearCertification: Certification
    extinguishingEquipmentCertification: Certification
    airTank2YearCertification: Certification
    electricEngineCertification: Certification
    ndtCertification: Certification
    airTank10YearCertification: Certification
    idleTimeHistoryData: idleTimeHistoryData[]
    fuelConsumptionHistoryData: fuelConsumptionHistoryData[]
    inspectionData: inspectionData[]
    idleHoursThresholdValues: idleHoursThresholdValues
    inspectionDataOfLast10Weeks: inspectionDataOfLast10Weeks[]
    hasAccessToCompany: boolean
    trainings: Training[]
    linkedMachines?: Machine[]
    nextService?: MachineService
}

export interface MachineService {
    dueHours?: string
    workOrderStatus?: string
    status?: string
}

export interface MachineTable {
    data: string;
    id: string;
    categoryImage?: string;
}

export interface MachinePayload {
    id?: number
    name?: string
    intern?: string
}

export interface idleTimeHistoryData {
    day: string
    previousWorkingDay: string
    previousWorkDayPercentageBySubcategory: number
    previousWorkDayPercentage: number
    previousWeekPercentageBySubcategory: number
    previousWeekPercentage: number
    last12MonthsPercentageBySubcategory: number
    last12MonthsPercentage: number
    TargerPercentageBySubcategory: number
    previousWorkDayPerformanceCompareToAverage: number
    previousWeekPerformanceCompareToAverage: number
    last12MonthsPerformanceCompareToAverage: number
    lowThresholdEnd: number
    middleThresholdEnd: number
    highThresholdEnd: number
}

export interface idleHoursThresholdValues {
    lowThresholdEnd: number
    middleThresholdEnd: number
    highThresholdEnd: number
}

export interface fuelConsumptionHistoryData {
    previousWorkingDay: string
    previousWorkDayConsumptionBySubcategory: number
    previousWorkDayConsumption: number
    previousWeekConsumptionBySubcategory: number
    previousWeekConsumption: number
    last12MonthsConsumptionBySubcategory: number
    last12MonthsConsumption: number
    previousWorkDayPerformanceCompareToAverage: number
    previousWeekPerformanceCompareToAverage: number
    last12MonthsPerformanceCompareToAverage: number
}

export interface inspectionData {
    previousWorkingDay: string
    TargerPercentage: number
    last10WeeksPeriodicChecksBySubcategory: number
    last10WeeksPeriodicChecks: number
    last6MonthsPeriodicChecksBySubcategory: number
    last6MonthsPeriodicChecks: number
    last12MonthsPeriodicChecksBySubcategory: number
    last12MonthsPeriodicChecks: number
    previous10WeeksPerformanceCompareToAverage: number
    last6MonthsPerformanceCompareToAverage: number
    last12MonthsPerformanceCompareToAverage: number
}

export interface inspectionDataOfLast10Weeks {
    isFilled: boolean
    value: number
}

export interface chartData {
    value: number
    color?: string
}

export interface legendData {
    id: string,
    value: string,
    icon: MaterialIcons
}
//forgot password
export interface ForgotPasswordPayload {
    username?: string
}

export interface ForgotPasswordResponse {
    success?: boolean
    message: string
}

//issue payload
export interface IssuePayload {
    id?: string | number
    registrationDate?: string
    issueType: 'issue' | 'repair'
    machineId: Machine['id']
    projectId: Project['id']
    oldDepartmentId?: Department['id']
    departmentId?: Department['id']
    performedBy?: User['id']
    categoryId: IssueCategory['id']
    categoryName?: IssueCategory['name']
    type: string
    sortingType?: number
    inspectionType?: number
    description?: string
    images?: (string | File)[]
    files?: (string | File)[] | ''
    sync?: number
    guid?: string
    isCreatedFromOfflineMode?: boolean
    sourceTypeId?: number
    updateFiles?: number | boolean
}

export interface IssueResponsePayload {
    id: Issue['id']
    type: Issue['type']
    offlineIssueId?: Issue['id']
}

export type OperatingUnitTypePayload = OperatingTypes.OPERATING_TIME | OperatingTypes.OPERATING_COUNT;

export interface IssueImagePayload {
    id?: string | number
    images?: (string | File)[]
}

export interface IssueClosePayload {
    issueId?: number
    issueCloseType?: number
    comment: string
    operatingTime?: number
    operatingCount?: number
    sortingType?: number
    hpCode?: number
    workType?: number
    costCategory?: number
    customer?: number
    project?: number
    ordinaryHours?: number | string
    overTime50?: number | string
    overTime100?: number | string
}

export interface ServiceClosePayload {
    workOrderId?: number
    serviceId?: number
    comment?: string
    operatingTime?: number
    operatingCount?: number
    sortingType?: number
    hpCode?: number
    workType?: number
    costCategory?: number
    customer?: number
    project?: number
    nextDynamicServiceOccurrence?: number
    nextDynamicScheduleAt?: Date
    nextAlertServiceOccurrence?: number
    nextAlertScheduleDays?: number
    ordinaryHours?: number | string
    overTime50?: number | string
    overTime100?: number | string
}

export interface APIResponse {
    success: boolean
    msg?: string
}

export interface IssueCompletePayload {
    issueId?: number
    comment: string
    hpCode?: number
    ordinaryHours?: number | string
    overTime50?: number | string
    overTime100?: number | string
    workType: number
}

export interface ServiceCompletePayload {
    workOrderId?: number
    serviceId?: number
    comment: string
    nextDynamicServiceOccurrence?: number
    nextDynamicScheduleAt?: Date
    nextAlertServiceOccurrence?: number
    nextAlertScheduleDays?: number
    hpCode?: number
    ordinaryHours?: number | string
    overTime50?: number | string
    overTime100?: number | string
    workType: number
}

export interface ServiceSavePayload {
    workOrderId?: number
    serviceId?: number
    checklist: ServiceCheck[]
    /** 1 when the user taps Temporary save. Complete and close do not send this. */
    isDraft?: number
}

export interface IssueChatPayload {
    issueId: number,
    userIds: number[]
}

//periodic check payload
/** Sent with upload so create date matches the file on the device (not server upload time). */
export interface PeriodicCheckImageMeta {
    dc?: string
    sz?: number
}

export interface PeriodicCheckImage {
    id?: string | number
    url: string
    order?: number
    dc?: string
    sz?: number
}

export interface PeriodicCheckPayload extends PeriodicCheck {
    status: 0 | 1 | 2 | 3 | null
    quantity: number
    issues: IssueResponsePayload[]
    images?: (string | File | PeriodicCheckImage)[]
    im?: PeriodicCheckImageMeta[]
    sync?: number
    existingImages?: string[]
    existingIm?: PeriodicCheckImageMeta[]
    newImages?: File[]
    newIm?: PeriodicCheckImageMeta[]
}

export interface PeriodicChecksPayload {
    id?: string
    machineId: Machine['id']
    projectId: Project['id']
    inspectionType: Machine['inspectionType']
    checks: PeriodicCheckPayload[]
    guid?: string
    isDraft?: number
}

export interface PeriodicCheckDraftResponse {
    id: string
    checks: PeriodicCheckPayload[]
    inspectionType: number
    machineId?: Machine['id']
    projectId?: Project['id']
}

export interface PeriodicCheckSaveResponse {
    id?: string
    success?: boolean
    checkData?: PeriodicCheckDraftResponse
    /** True when the save was written to the local sync queue instead of the server. */
    queued?: boolean
}

export interface ServiceCheckPayload extends ServiceCheck {
    status: 0 | 1 | 2 | null
    issues: string[]
    sync?: number
}

export interface ServiceChecksPayload {
    machineId: Machine['id']
    serviceId: Service['id']
    checks: ServiceCheckPayload[]
    guid?: string
}

export interface syncResponse {
    IssuePayload?: number,
    PeriodicChecks?: number
    OperatingTimeLog?: number
    issueIds?: Issue['id'][]
    updatedIssuesDetails?: IssuePayload[]
}

//documentation payload
export interface DocumentationTabs {
    activeTab: 'specifications' | 'equipments' | 'archives'
}

export interface MachineSpecification {
    id: Machine['id']
    intern: Machine['intern']
    regNo: Machine['regNo']
    model?: string
    serialNo?: string
    fixedDriverName?: string
    categoryName?: string
    subCategoryName?: string
    status?: string
    hovedNo?: string
    ledig10?: string
    purchaseDate?: string
    operatingTime?: string
    operatingCount?: string
    ledig7?: string
    ledig8?: string
    ledig9?: string
    dayRental?: string
    diesel?: string
    isScheduling?: string
    autopass?: string
    extraKey?: string
    insurance?: string
    info?: string
    size?: string
    type?: string
    isExternal?: string
    currentDriverName?: string
    handedOutDate?: string
    gangtidUpdatedAt?: string
    operatingCountUpdatedAt?: string
    permit?: string
    certificationType?: string
    certificationLastDate?: string
    certificationNextDate?: string
    certificationIsApproved?: string
    adrType?: string
    adrLastDate?: string
    adrNextDate?: string
    adrStatus?: string
    liftControlCertificationType?: string
    liftControlCertificationLastDate?: string
    liftControlCertificationNextDate?: string
    liftControlCertificationisApproved?: string
    electricalControlCertificationType?: string
    electricalControlCertificationLastDate?: string
    electricalControlCertificationNextDate?: string
    electricalControlCertificationisApproved?: string
    psvCertificationType?: string
    psvCertificationLastDate?: string
    psvCertificationNextDate?: string
    psvCertificationisApproved?: string
    airTank1YearCertificationType?: string
    AirTank1YearCertificationLastDate?: string
    AirTank1YearCertificationNextDate?: string
    AirTank1YearCertificationisApproved?: string
    extinguishingEquipmentCertificationType?: string
    extinguishingEquipmentCertificationLastDate?: string
    extinguishingEquipmentCertificationNextDate?: string
    extinguishingEquipmentCertificationisApproved?: string
    airTank2YearCertificationType?: string
    airTank2YearCertificationLastDate?: string
    airTank2YearCertificationNextDate?: string
    airTank2YearCertificationisApproved?: string
    electricEngineCertificationType?: string
    electricEngineCertificationLastDate?: string
    electricEngineCertificationNextDate?: string
    electricEngineCertificationisApproved?: string
    ndtCertificationType?: string
    ndtCertificationLastDate?: string
    ndtCertificationNextDate?: string
    ndtCertificationisApproved?: string
    airTank10YearCertificationType?: string
    AirTank10YearCertificationLastDate?: string
    AirTank10YearCertificationNextDate?: string
    AirTank10YearCertificationisApproved?: string
    manufacturerName?: string
    modelName?: string
    liter?: string
    lightOpening?: string
    teeth?: string
    typeTooth?: string
    knife?: string
    comment?: string
    weight?: string
    rentDate?: string
    projectNumber?: string
    projectName?: string
    updatedAt?: string
    driverName?: string
    driverPhone?: string
    ledig1?: string
    ledig2?: string
    ledig3?: string
    ledig4?: string
    ledig5?: string
    classification?: string
    trackUnitName?: string
    trackUnitMachineId?: string
    isActive?: string
    periodicCheckBonusCode?: string
    isDummyAsset?: string
    quickCouplingAttachmentSize?: string
    quickCouplingSerialNumber?: string
    engineType?: string
    engineSerialNumber?: string
    tiltrotatorSerialNumber?: string
    isAvailableForKpiReporting?: string
    consideredForSale?: string
    name: Machine['name']
    owner?: string
    cm?: string
    employeeCategory?: string
    driver?: string
    manufacturerInfo?: string
    isGps?: string
}

export interface Equipment {
    id: string
    name: string
    intern: string
}

export interface Archive {
    id: number
    name: string
    description: string
    type: number
    docType: number
    fileType: string
    fileName: string | null
    filePath: string
    downloadLink: string
    mimeType: string
    parentId: number
    machineId: string
    lastUpdatedAtForSorting: string
    documentId?: string
}

export interface ArchiveTree extends Archive {
    child?: ArchiveTree[]
}

export interface ArchivePayload {
    machineId: Machine['id']
    parentFolderId: Archive['id']
}

export interface AllTenantListItem {
    id: string,
    name: string
}

export interface Notification {
    id: string
    machineId: string
    intern: string
    category: string
    subcategory: string
    issueId: string
    issueDescription: string
    senderId: string
    senderName: string
    senderImage: string
    message: string
    createdAt: string
    updatedAt: string
    messageType: number
    messageStatus: boolean,
    importance: number
    roleName?: string | null
}

export interface Pagination {
    totalRecords: number
}

export interface NotificationData {
    userNotifications: Notification[]
    pagination: Pagination
}

export interface NotificationPayload {
    userId?: number
    offset?: number
    searchText?: string
    chatTypes?: number[]
}

export interface UnreadNotificationCount {
    unreadNotificationCount: number
    perTypeCount?: Record<number, number>
}

export interface UpdateNotificationStatusPayload {
    issueId: IssueDetails['id']
    type: MessageType
    messageId?: number 
}

export interface UpdateNotificationsData {
    messageId?: number
    issueId: IssueDetails['id']
    numberOfNotificationsReads: number
    type?: number
}

export interface DepartmentChangeWebSocketData {
    issueId: IssueDetails['id'], 
    oldDepartmentId: IssueDetails['issueDepartmentId'], 
    newDepartmentId: FieldValue | undefined
}

export interface CancelledServiceWebSocketData {
    serviceId: ServiceDetails['id'], 
    departmentId: ServiceDetails['departmentId']
    machineId: ServiceDetails['machineId']
}

export interface DeletedServiceWebSocketData {
    serviceId: ServiceDetails['id'], 
    departmentId: ServiceDetails['departmentId']
}

export interface ClosedIssueWebSocketData {
    issueId: IssueDetails['id'], 
    departmentId: IssueDetails['issueDepartmentId']
}

export interface SavePartPayload {
    id: string,
    parts: Part[]
}

export interface SavePartResponse {
    success?: boolean
    message: string
}

export interface SearchPartResponse {
   parts: Part[],
   total: number
}

export enum MachineOppgaverType {
    ISSUE = 0,
    SERVICE = 1,
    WORKORDER = 2
}

export interface MachineOppgaver {
    id: string
    name: string
    intern: string
    image: string
    tasks: MachineOppgaverIssue[]
}

export interface MachineOppgaverIssue {
    type: string,
    count: number
} 

export interface TaskPayload {
    offset: number,
    filters: FilterTaskPayload
    userRole: number
}

export interface FilterTaskPayload {
    projects: string[]
    departments: string[]
    responsible: string[]
    searchText: string
}

export interface TaskFilterOptions {
    projects: TaskFilterOption[]
    departments: TaskFilterOption[]
}

export interface TaskFilterOption {
    id: number
    name: string
    number: number
}

export interface SaveChatUserResponse {
    success: boolean,
    message: string
}

export interface SaveChatUserPayload {
    users: SearchUser[],
    issueId: string
}

export interface UserProfilePayload {
    image: (string | File),
    action: 'upload' | 'remove'
}

export interface UserProfileResponse {
    success: boolean
    avatar: string
    image: string
}

export interface SaveChatUserResponse {
    success: boolean,
    message: string
}

export interface SaveChatUserPayload {
    users: SearchUser[],
    issueId: string
}

// Communication ========================================================================================================
export interface CommunicationRoleUser {
    id: number
    name: string
}

/**
 * A role row served from the backend ACL table for the Kommunikasjon screen.
 * Driven entirely by `GET /driver-api/communication/roles` so adding/removing
 * roles in the database is reflected on mobile without code changes.
 */
export interface CommunicationRole {
    id: number
    name: string
    description?: string | null
    primaryName?: string | null
    userCount: number
    users: CommunicationRoleUser[]
}

export interface CommunicationChatDetails {
    id: string | number
    machineId: string | number
    projectId: string | number
    machineIntern: string
    machineName: string
    projectNumber?: string
    projectName?: string
    roleId?: number
    recipientUserIds?: number[]
    recipients?: CommunicationChatRecipient[]
    title?: string
    roleName?: string
    categoryName?: string
    categoryImage?: string
    chat: Message[]
    chatUsers: SearchUser[]
}

export interface CommunicationChatRecipient {
    id: number
    name: string
}

export interface OpenCommunicationChatPayload {
    machineId: string | number
    projectId: string | number
    /** ACL role id from Kommunikasjon role picker (Operator, Manager, …). */
    roleId: number
    userIds: number[]
    recipients?: CommunicationChatRecipient[]
}

export enum EnvironmentalDashboardCalculationMode {
    INDIVIDUAL_PERFORMANCE = 1,
    CATEGORY_BENCHMARK = 2,
}

export const DEFAULT_DAILY_INSPECTION_TARGET = 100;
export const DEFAULT_ENVIRONMENTAL_DASHBOARD_CALCULATION_MODE = EnvironmentalDashboardCalculationMode.INDIVIDUAL_PERFORMANCE;